import React, { useState } from 'react';
import { updateProfile, sendPasswordResetEmail, verifyBeforeUpdateEmail } from 'firebase/auth';
import { auth } from './firebaseConfig';
import './ProfilePage.css';

const ProfilePage = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.displayName || '');
  const [newEmail, setNewEmail] = useState(user?.email || '');

  // 1. Update Name Logic
  const handleUpdateProfile = async () => {
    try {
      await updateProfile(user, { displayName: name });
      setIsEditing(false);
      alert("Display name updated!");
    } catch (e) { alert(e.message); }
  };

  // 2. Password Reset Logic (Sends email to current user)
  // Inside your ProfilePage.js / handlePasswordReset function
const handlePasswordReset = async () => {
  if (!user?.email) return;

  // This tells Firebase where to go AFTER the password is reset
  const actionCodeSettings = {
    url: window.location.origin + '/login', // Dynamically gets ozia.netlify.app or ozia-ai.netlify.app
    handleCodeInApp: false
  };

  try {
    await sendPasswordResetEmail(auth, user.email, actionCodeSettings);
    alert(`Password reset link sent to ${user.email}`);
  } catch (e) {
    alert("Error: " + e.message);
  }
};
  // 3. Email Update Logic
  const handleEmailUpdate = async () => {
    if (newEmail === user.email) {
      alert("This is already your current email.");
      return;
    }
    try {
      // This sends a verification link to the NEW email
      await verifyBeforeUpdateEmail(user, newEmail);
      alert(`Verification email sent to ${newEmail}. Please verify to complete the change.`);
      setIsEditing(false);
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') {
        alert("For security, please log out and log back in before changing your email.");
      } else {
        alert(e.message);
      }
    }
  };

  return (
    <div className="profile-page-wrapper">
      <header className="profile-header">
        <h1>My Profile</h1>
        <p>Manage your developer identity and security credentials.</p>
      </header>

      <div className="profile-content-layout">
        <section className="profile-hero-card">
          <div className="profile-avatar-container">
            <div className="profile-avatar-glow">
              <img src="/default-pic.png" alt="Avatar" className="profile-avatar-img" />
            </div>
            <div className="profile-hero-text">
              <h2>{user?.displayName || "Ozia Developer"}</h2>
              <span className="profile-status-tag">Verified Account</span>
            </div>
          </div>
        </section>

        <div className="profile-details-grid">
          {/* Identity Card */}
          <section className="profile-data-card">
            <div className="profile-card-header">
              <i className="fas fa-id-badge"></i>
              <h3>Personal Information</h3>
            </div>
            <div className="profile-card-body">
              <div className="profile-input-group">
                <label>Display Name</label>
                <input 
                  type="text" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  disabled={!isEditing}
                  className={`profile-input ${!isEditing ? 'is-locked' : 'is-editable'}`}
                />
              </div>
              <div className="profile-input-group">
                <label>Email Address</label>
                <input 
                  type="email" 
                  value={isEditing ? newEmail : user?.email} 
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={!isEditing}
                  className={`profile-input ${!isEditing ? 'is-locked' : 'is-editable'}`}
                />
              </div>
              
              {isEditing ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                   <button className="profile-main-btn save-btn" onClick={() => {
                     handleUpdateProfile();
                     if (newEmail !== user.email) handleEmailUpdate();
                   }}>
                    Save All Changes
                  </button>
                  <button className="profile-sec-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              ) : (
                <button className="profile-main-btn edit-btn" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </button>
              )}
            </div>
          </section>

          {/* Security Card */}
          <section className="profile-data-card">
            <div className="profile-card-header">
              <i className="fas fa-key"></i>
              <h3>Security</h3>
            </div>
            <div className="profile-card-body">
              <p className="profile-card-desc">We will send a secure password reset link to your registered email address.</p>
              <button className="profile-sec-btn" onClick={handlePasswordReset}>
                Send Password Reset Link
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;